import type { CSSProperties, ElementType, ReactNode } from "react";
import {
  webTypography,
  type WebTypographyVariant as TypographyVariant,
} from "../theme/typography.web";

export interface TypographyProps {
  variant?: TypographyVariant;
  color?: string;
  children: ReactNode;
  /** DOM element override. Defaults per-variant (h1/h2/h3 for headings,
   *  span for everything else). */
  as?: ElementType;
  className?: string;
  style?: CSSProperties;
  numberOfLines?: number;
}

const variantColors: Partial<Record<TypographyVariant, string>> = {
  display: "var(--text-primary)",
  headline: "var(--text-primary)",
  title: "var(--text-primary)",
  subtitle: "var(--text-primary)",
  subtitleSmall: "var(--text-primary)",
  body: "var(--text-primary)",
  bodySmall: "var(--text-secondary)",
  caption: "var(--text-muted)",
  button: "var(--text-on-brand)",
  eyebrow: "var(--intent-brand-text)",
  headlineHi: "var(--text-primary)",
  titleHi: "var(--text-primary)",
  bodyHi: "var(--text-primary)",
  captionHi: "var(--text-muted)",
};

const defaultElement: Record<TypographyVariant, ElementType> = {
  display: "h1",
  headline: "h1",
  title: "h2",
  subtitle: "h3",
  subtitleSmall: "h4",
  body: "p",
  bodySmall: "span",
  caption: "span",
  eyebrow: "p",
  button: "span",
  headlineHi: "h1",
  titleHi: "h2",
  bodyHi: "p",
  captionHi: "span",
};

export default function Typography({
  variant = "body",
  color,
  children,
  as,
  className,
  style,
  numberOfLines,
}: TypographyProps) {
  const Tag = as ?? defaultElement[variant];
  const clampStyle: CSSProperties | undefined = numberOfLines
    ? {
        display: "-webkit-box",
        WebkitLineClamp: numberOfLines,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      }
    : undefined;
  const resolvedStyle: CSSProperties = {
    ...webTypography[variant],
    color: color ?? variantColors[variant] ?? "var(--text-primary)",
    ...clampStyle,
    ...style,
  };
  return (
    <Tag className={className} style={resolvedStyle}>
      {children}
    </Tag>
  );
}
